import { useEffect, useRef, useState, type RefObject } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { STAY_PROPERTY_TYPES } from "@majourneys/shared";
import { Icon } from "@majourneys/icons";
import { Container } from "../../../shared/ui/container";
import { HUB_EASE, HUB_FOLD } from "../../activities/components/hub-top";
import { openHubFilters, useHubFiltersCount } from "../../activities/components/hub-dialogs";
import { floatPanelStyle } from "../../../shared/ui/use-presence";
import { MoodGlyph } from "./mood-glyph";
import { uiZoom } from "../lib/motion";
import { useStayTypeCounts } from "../api/stays-query";
import { STAY_VIBES, VIBE_ICON, type StayVibe } from "../lib/vibes";
import { BEVEL_CHIP_ON, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";

const NAVY = "#110E39";

/** "The top of the page", with a few pixels of give for trackpad bounce. */
const CEILING = 4;

/** The bottom edge of the bar, where it is right now: under the rail. */
export function barLine(): number {
  const rail = document.querySelector<HTMLElement>("[data-hub-rail]");
  const barH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--mj-stays-bar-h")) || 56;
  return (rail?.getBoundingClientRect().bottom ?? 0) + barH * uiZoom();
}
const META = "#5a5d77";

export interface StaysStickyBarProps {
  lang: string;
  /** "stays" on the All hub, otherwise the property type or "camp". */
  activeType: string;
  /** Params the type links carry over (dates, party). */
  carriedSearch?: Record<string, unknown>;
  /** Pinned only once this element has scrolled up under the rail. */
  sentinelRef: RefObject<HTMLElement | null>;
  vibes: StayVibe[];
  vibeCounts: Record<StayVibe, number>;
  onVibesChange: (next: StayVibe[]) => void;
  map?: { on: boolean; onChange: (next: boolean) => void };
  /** The city the list is narrowed to, for the summary. */
  place?: string | null;
}

/**
 * Option C, "one summary pill" (owner, 2026-09-28). Once the page's own
 * controls scroll away, the bar pins under the category rail holding a single
 * centred pill that says what is chosen:
 *
 *              ( Riads · Marrakech · Pool day · 2 filters  = )   [List]
 *
 * Tapping it drops a panel with everything: the types, the moods, search,
 * Filters and Map. The calmest bar of the three tried; every control is still
 * one tap away.
 *
 * Fixed rather than sticky, and moved by transform only. It folds with the
 * rail through the same `html[data-hub-compact]` switch and the same curve, so
 * the two never drift apart, and nothing here changes size while it moves:
 * height animations stutter on an iPhone.
 */
export function StaysStickyBar({
  lang,
  activeType,
  carriedSearch = {},
  sentinelRef,
  vibes,
  vibeCounts,
  onVibesChange,
  map,
  place,
}: StaysStickyBarProps) {
  const filterCount = useHubFiltersCount();
  const { t } = useTranslation();
  const counts = useStayTypeCounts(true);
  const [shown, setShown] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  /*
   * Option A (owner, 2026-09-28, replacing the scroll stop): the bar follows
   * the guest's direction. Scrolling down, it hides with the folding header
   * and the list gets the whole screen; the smallest move up brings it back,
   * anywhere in the list. It only ever shows while the page's own controls are
   * out of sight, so the two are never on screen together.
   *
   * Direction comes from the category rail's scroll hook (`html[data-hub-dir]`),
   * so the bar and the rail read the same scroll. In map view the rail stays in
   * its slim row on the way up and the bar sits right under it; in list view
   * the header and full rail come back and the bar sits under those.
   */
  useEffect(() => {
    const root = document.documentElement;
    let frame = 0;
    const check = () => {
      frame = 0;
      const el = sentinelRef.current;
      if (!el) return;
      // Shown on a scroll up, hidden on a scroll down, and only while the
      // page's own controls are out of sight behind the (slim) rail and bar.
      const up = root.dataset.hubDir === "up";
      // Where the bar's bottom edge will sit once the rail settles: under the
      // slim row (map view, or still folded) or under header + full rail. Read
      // from layout, not the rail's live rect, which is mid-slide right now.
      const rail = document.querySelector<HTMLElement>("[data-hub-rail]");
      const railH = rail?.offsetHeight ?? 86;
      const headerH = parseFloat(getComputedStyle(root).getPropertyValue("--mj-header-h")) || 69;
      const barH = parseFloat(getComputedStyle(root).getPropertyValue("--mj-stays-bar-h")) || 56;
      const folded = root.dataset.hubCompact !== undefined;
      const line = ((folded ? railH - HUB_FOLD : headerH + railH) + barH) * uiZoom();
      setShown(up && window.scrollY > CEILING && el.getBoundingClientRect().bottom <= line);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    // The fold flips on an attribute; follow it the moment it changes.
    const mo = new MutationObserver(schedule);
    mo.observe(root, { attributes: true, attributeFilter: ["data-hub-compact", "data-hub-dir"] });
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      mo.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [sentinelRef]);


  /* Tell the page when the bar is up, so the pinned map can make room for it. */
  useEffect(() => {
    const root = document.documentElement;
    if (shown) root.dataset.staysBar = "";
    else delete root.dataset.staysBar;
    return () => {
      delete root.dataset.staysBar;
    };
  }, [shown]);

  /* Its height, for the map that pins beneath it. */
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--mj-stays-bar-h", `${bar.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(bar);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--mj-stays-bar-h");
    };
  }, []);

  /* The type menu closes on a click outside it, on Escape, and when the bar hides. */
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);
  useEffect(() => {
    if (!shown) setMenuOpen(false);
  }, [shown]);

  const types = [
    { slug: "stays", label: t("common.all") },
    // Camps are an activity category, not a supplier type, and the type row
    // above the list offers them too. Plural and right after "All", as in
    // the app.
    { slug: "camp", label: t("stays.camps") },
    ...STAY_PROPERTY_TYPES.filter((type) => (counts?.get(type) ?? 1) > 0)
      .sort((a, b) => (counts?.get(b) ?? 0) - (counts?.get(a) ?? 0))
      .map((type) => ({ slug: type, label: t(`stays.types.${type}`) })),
  ];
  const current = types.find((type) => type.slug === activeType) ?? types[0]!;
  const tab = shown ? undefined : -1;

  /* "Riads · Marrakech · Pool day · 2 filters": two moods by name, then +n. */
  const moodNames = vibes.map((v) => t(`stays.vibes_short.${v}`));
  const summary = [
    current.label,
    place,
    moodNames.length > 2 ? `${moodNames.slice(0, 2).join(", ")} +${moodNames.length - 2}` : moodNames.join(", "),
    filterCount ? t("stays.hub_filters.n_filters", { count: filterCount }) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const button =
    "inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full px-4 text-[13px] font-semibold transition-[transform,background,color] duration-150 active:scale-[0.97]";
  const off = { background: BEVEL_WHITE_FACE, color: NAVY, boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px rgba(17,14,57,0.12)` } as const;
  const on = { background: BEVEL_NAVY_FACE, color: "#fff", boxShadow: BEVEL_CHIP_ON } as const;

  return (
    <div
      className="mj-stays-bar pointer-events-none fixed inset-x-0 z-20"
      style={{ top: "calc(var(--mj-header-h, 69px) + var(--mj-hub-rail-h, 86px))" }}
    >
      <style>{`
        .mj-stays-bar{transition:transform 320ms ${HUB_EASE}}
        html[data-hub-compact] .mj-stays-bar{transform:translateY(calc(-1 * (var(--mj-header-h, 69px) + ${HUB_FOLD}px)))}
      `}</style>
      <div
        ref={barRef}
        aria-hidden={!shown}
        className="border-b"
        style={{
          transform: shown ? "translateY(0)" : "translateY(-100%)",
          opacity: shown ? 1 : 0,
          transition: `transform 280ms ${HUB_EASE}, opacity 200ms ease`,
          pointerEvents: shown ? "auto" : "none",
          // Solid, not frosted: a see-through bar let the chips it covers show
          // through as ghosts.
          background: "#F8F9FC",
          borderColor: "rgba(17,14,57,0.08)",
        }}
      >
        <Container>
          <div ref={menuRef} className="relative flex h-14 items-center justify-center gap-2">
            {/* The summary pill: the whole bar at rest. */}
            <button
              type="button"
              tabIndex={tab}
              aria-expanded={menuOpen}
              aria-haspopup="dialog"
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex min-h-10 py-1.5 min-w-0 max-w-full items-center gap-2 rounded-full bg-white px-4 text-[14px] transition-transform active:scale-[0.98] lg:max-w-[620px]"
              style={{ color: NAVY, boxShadow: "0 0 0 1px rgba(17,14,57,0.12)" }}
            >
              <Icon name="ri-search-line" className="shrink-0 text-[16px]" aria-hidden="true" />
              <span className="min-w-0 break-words leading-tight font-semibold">{summary}</span>
              <Icon
                name="ri-equalizer-line"
                className="shrink-0 text-[16px]"
                style={{ transform: menuOpen ? "rotate(90deg)" : "none", transition: `transform 200ms ${HUB_EASE}` }}
                aria-hidden="true"
              />
            </button>
            {map ? (
              <button
                type="button"
                aria-pressed={map.on}
                onClick={() => map.onChange(!map.on)}
                tabIndex={tab}
                className={`${button} hidden lg:inline-flex`}
                style={map.on ? on : off}
              >
                <Icon name={map.on ? "ri-layout-grid-line" : "ri-map-2-line"} className="text-[15px]" aria-hidden="true" />
                {map.on ? t("stays.hub_map.hide") : t("stays.hub_map.show")}
              </button>
            ) : null}

            {/* Everything, dropped under the pill. A panel hanging over the
                page, so it wears the house 4px pewter band (owner, 2026-10-11:
                every band the same width); the choices inside are plain
                controls, white with one hairline, navy when on. */}
            {/* Always mounted, so it can leave the way it came: it grows out
                of the pill (origin at the pill, top centre) and shrinks back
                into it. Hidden from focus and clicks while closed. */}
            {(
              <div
                role="dialog"
                aria-label={t("filter.filters", { defaultValue: "Filters" })}
                aria-hidden={!menuOpen}
                inert={!menuOpen}
                className="absolute inset-x-0 top-[calc(100%+8px)] mx-auto w-full max-w-[680px] p-5 motion-reduce:[transform:none!important]"
                style={{
                  ...floatPanelStyle(28),
                  transformOrigin: "50% 0",
                  opacity: menuOpen ? 1 : 0,
                  transform: menuOpen ? "none" : "translateY(-6px) scale(0.97)",
                  pointerEvents: menuOpen ? "auto" : "none",
                  visibility: menuOpen ? "visible" : "hidden",
                  transition: `opacity 180ms ${HUB_EASE}, transform 200ms ${HUB_EASE}, visibility 0s linear ${menuOpen ? "0ms" : "200ms"}`,
                }}
              >
                <p className="m-0 mb-2.5 text-[12px] font-bold uppercase tracking-[0.08em]" style={{ color: META }}>
                  {t("stays.hub_filters.type_title")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {types.map((type) => {
                    const active = type.slug === activeType;
                    return (
                      <Link
                        key={type.slug}
                        to="/$lang/category/$category"
                        params={{ lang, category: type.slug }}
                        search={carriedSearch as never}
                        resetScroll={false}
                        onClick={() => setMenuOpen(false)}
                        aria-current={active ? "page" : undefined}
                        className={`${button} no-underline`}
                        style={active ? on : off}
                      >
                        {type.label}
                      </Link>
                    );
                  })}
                </div>

                <p className="m-0 mb-2.5 mt-5 text-[12px] font-bold uppercase tracking-[0.08em]" style={{ color: META }}>
                  {t("stays.vibes.label")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {STAY_VIBES.map((v) => {
                    const active = vibes.includes(v);
                    if (!active && vibeCounts[v] === 0) return null;
                    return (
                      <button
                        key={v}
                        type="button"
                        aria-pressed={active}
                        onClick={() => onVibesChange(active ? vibes.filter((x) => x !== v) : [...vibes, v])}
                        className={button}
                        style={active ? on : off}
                      >
                        <MoodGlyph icon={VIBE_ICON[v]} on={active} size={15} />
                        {t(`stays.vibes.${v}`)}
                        <span style={{ opacity: 0.65, fontWeight: 500 }}>{vibeCounts[v]}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-2 border-t pt-5" style={{ borderColor: "rgba(17,14,57,0.08)" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={button}
                    style={off}
                  >
                    <Icon name="ri-search-line" className="text-[15px]" aria-hidden="true" />
                    {t("explore.where_to")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      openHubFilters();
                    }}
                    className={button}
                    style={off}
                  >
                    <Icon name="ri-equalizer-line" className="text-[15px]" aria-hidden="true" />
                    {t("filter.filters", { defaultValue: "Filters" })}
                    {filterCount ? (
                      <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-bold text-white" style={{ background: NAVY }}>
                        {filterCount}
                      </span>
                    ) : null}
                  </button>
                  {map ? (
                    <button
                      type="button"
                      aria-pressed={map.on}
                      onClick={() => {
                        setMenuOpen(false);
                        map.onChange(!map.on);
                      }}
                      className={button}
                      style={map.on ? on : off}
                    >
                      <Icon name={map.on ? "ri-layout-grid-line" : "ri-map-2-line"} className="text-[15px]" aria-hidden="true" />
                      {map.on ? t("stays.hub_map.hide") : t("stays.hub_map.show")}
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>
    </div>
  );
}
